import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, PencilSimple, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";

const emptyCourse = { name: "", code: "", duration_days: 365, fee_amount: 0, description: "", offers_placement: true };

export default function Courses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // course object when editing, null when creating
  const [form, setForm] = useState(emptyCourse);
  const [submitting, setSubmitting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () =>
    api
      .get("/courses", { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyCourse);
    setOpen(true);
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, code: c.code, duration_days: c.duration_days, fee_amount: c.fee_amount, description: c.description || "", offers_placement: c.offers_placement !== false });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/courses/${editing.id}`, {
          name: form.name,
          duration_days: parseInt(form.duration_days, 10),
          fee_amount: parseFloat(form.fee_amount),
          description: form.description,
          offers_placement: !!form.offers_placement,
        });
        toast.success("Course updated");
      } else {
        await api.post("/courses", {
          ...form,
          duration_days: parseInt(form.duration_days, 10),
          fee_amount: parseFloat(form.fee_amount),
          offers_placement: !!form.offers_placement,
        });
        toast.success("Course created");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyCourse);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const archive = async (c) => {
    try {
      await api.post(`/courses/${c.id}/archive`);
      toast.success(`${c.name} archived`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const unarchive = async (c) => {
    try {
      await api.post(`/courses/${c.id}/unarchive`);
      toast.success(`${c.name} restored`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Catalog</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Courses.</h1>
          <p className="text-slate-500 mt-2">{items.filter((c) => !c.archived).length} active programme{items.filter((c) => !c.archived).length === 1 ? "" : "s"}.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-xs font-mono-sms uppercase tracking-[0.15em] text-slate-600">
            <input type="checkbox" data-testid="toggle-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-[var(--sms-primary)]" />
            Show archived
          </label>
          <button onClick={startCreate} data-testid="btn-new-course" className="sms-btn-primary inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Course
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="courses-grid">
        {items.map((c) => (
          <div key={c.id} data-testid={`course-${c.code}`} className={`sms-card p-6 ${c.archived ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display text-xl font-bold tracking-tight">{c.name}</div>
                  {c.archived && <span className="sms-badge sms-badge-rejected">Archived</span>}
                  {c.offers_placement === false && (
                    <span data-testid={`no-placement-${c.code}`} className="font-mono-sms text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-slate-300 text-slate-600">
                      No placement
                    </span>
                  )}
                </div>
                <div className="font-mono-sms text-xs uppercase tracking-[0.15em] text-[var(--sms-primary)] mt-1">
                  {c.code} · {c.duration_days} {c.duration_days === 1 ? "day" : "days"}
                </div>
              </div>
              <div className="font-mono-sms text-sm text-slate-900 shrink-0" data-testid={`course-${c.code}-fee`}>
                ₹{Number(c.fee_amount || 0).toLocaleString("en-IN")}
              </div>
            </div>
            {c.description && <p className="mt-4 text-sm text-slate-600">{c.description}</p>}
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
              <button onClick={() => startEdit(c)} data-testid={`edit-${c.code}`} className="sms-btn-secondary text-xs inline-flex items-center gap-1">
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
              {c.archived ? (
                <button onClick={() => unarchive(c)} data-testid={`unarchive-${c.code}`} className="sms-btn-secondary text-xs inline-flex items-center gap-1">
                  <ArrowCounterClockwise size={12} weight="bold" /> Restore
                </button>
              ) : (
                <button onClick={() => archive(c)} data-testid={`archive-${c.code}`} className="sms-btn-secondary text-xs inline-flex items-center gap-1">
                  <Archive size={12} weight="bold" /> Archive
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-slate-900 shadow-2xl w-full max-w-md p-8" data-testid="course-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-primary)]">{editing ? "Edit course" : "New course"}</div>
                <div className="font-display text-2xl font-black tracking-tighter mt-1">{editing ? editing.name : "Add to catalog"}</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="course-modal-close" className="text-slate-400 hover:text-slate-900">
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="space-y-4">
              <Field label="Name">
                <input className="sms-input" data-testid="course-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Code">
                  <input className="sms-input font-mono-sms uppercase disabled:opacity-60" data-testid="course-code" required disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </Field>
                <Field label="Duration (days)">
                  <input type="number" min={1} className="sms-input" data-testid="course-duration" required value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
                </Field>
                <Field label="Fee (₹)">
                  <input type="number" min={0} step="0.01" className="sms-input" data-testid="course-fee" required value={form.fee_amount} onChange={(e) => setForm({ ...form, fee_amount: e.target.value })} />
                </Field>
              </div>
              <Field label="Description">
                <textarea rows={3} className="sms-input" data-testid="course-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  data-testid="course-offers-placement"
                  checked={!!form.offers_placement}
                  onChange={(e) => setForm({ ...form, offers_placement: e.target.checked })}
                  className="w-4 h-4 mt-1 accent-[var(--sms-primary)]"
                />
                <div>
                  <div className="text-sm font-semibold">Offers placement</div>
                  <div className="text-[11px] text-slate-500">
                    When off, this course is treated as completion-only — the Placement workflow is hidden and only the standard course-completion certificate is issued.
                  </div>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
              <button type="button" onClick={() => setOpen(false)} className="sms-btn-secondary">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="course-submit" className="sms-btn-primary">
                {submitting ? "Saving…" : editing ? "Save changes" : "Create course"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}
